import React, { useEffect, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import sessionManager from '../utils/sessionManager';
import Unauthorized from './Unauthorized';

interface AuthWrapperProps {
  children: React.ReactNode;
}

const AuthWrapper: React.FC<AuthWrapperProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [errorMessage, setErrorMessage] = useState<string>('');

  useEffect(() => {
    const checkAuth = async () => {
      try {
        // 1. Extract token from URL query parameters or path
        const urlParams = new URLSearchParams(window.location.search);
        let token = urlParams.get('token');

        // Fallback: Check if token is in the path (e.g. /token=...)
        if (!token) {
          const match = window.location.href.match(/token=([^&/#?]+)/);
          if (match && match[1]) {
            token = match[1];
            console.log('Token found in URL path:', token);
          }
        }

        if (token) {
          // 2. Authenticate with token
          console.log('Token found in URL, authenticating...');
          const result = await sessionManager.authenticateWithToken(token);

          if (result.valid) {
            console.log('Authentication successful');
            setIsAuthenticated(true);

            // Remove token from URL for security
            // Remove token from URL for security
            const newUrl = new URL(window.location.href);
            newUrl.searchParams.delete('token');

            // Also clean from path if it was there
            let cleanUrl = newUrl.toString();
            if (cleanUrl.includes('token=')) {
              cleanUrl = cleanUrl.replace(/\/token=[^&/#?]+/, '').replace(/token=[^&/#?]+/, '');
            }

            window.history.replaceState({}, document.title, cleanUrl);
          } else {
            console.error('Token authentication failed:', result.message);
            setErrorMessage(result.message || 'Invalid or expired token');
            setIsAuthenticated(false);
          }
        } else {
          // 3. No token in URL, check for existing session
          console.log('No token in URL, checking for existing session...');
          const result = await sessionManager.validateSession();

          if (result.valid) {
            console.log('Valid session found');
            setIsAuthenticated(true);
          } else {
            console.log('No valid session found');
            setErrorMessage(result.message || 'No valid token or session found');
            setIsAuthenticated(false);
          }
        }
      } catch (error) {
        console.error('Authentication check error:', error);
        setErrorMessage('An error occurred during authentication');
        setIsAuthenticated(false);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, []);

  // Show loading spinner while checking authentication
  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          gap: 2
        }}
      >
        <CircularProgress size={60} />
        <Typography variant="h6" color="text.secondary">
          Verifying authentication...
        </Typography>
      </Box>
    );
  }

  // Show unauthorized page if not authenticated
  if (!isAuthenticated) {
    return <Unauthorized message={errorMessage} />;
  }

  // Render children if authenticated
  return <>{children}</>;
};

export default AuthWrapper;
